import { BasicFieldType, ExtraFields, FormData } from '.';
import {
	NEUTRAL_CHIP,
	ACTIVE_CHIP,
	NEGATIVE_CHIP,
} from '../mixins/style/chip-mixins';
import { EventStatusCode } from './event';
import { JobStatusCode } from './job';
import { EmptyMultiLangText, MultiLangText } from './locale';
import { MultiStepFormValidationStatus } from './multi-step-form';

export type ProgramValidations = Partial<
	Record<ProgramEditStep, ProgramValidationStatus>
>;

export enum ProgramListType {
	draft = 'draft',
	published = 'published',
}

export enum ReportTypeCode {
	j2 = 'j2',
	j2a = 'j2a',
	j2b = 'j2b',
	e5 = 'e5',
	mtrc = 'mtrc',
	ns = 'ns',
}

export enum ReportTypeStatusCode {
	active = 'active',
	inactive = 'inactive',
}

export enum FundingStatus {
	created = 'created',
	draft = 'draft',
}

export enum ReportTypeFrequency {
	quarterly = 'quarterly',
}

export interface ReportType {
	id: string;
	reportTypeCode: ReportTypeCode;
	frequency: ReportTypeFrequency;
	gracePeriod: number;
	status: ReportTypeStatusCode;
	revisionName?: MultiLangText;
	revisionDate?: string; //yyyy-MM-dd
	templateUrl?: string;
	createdAt: Date;
	updatedAt: Date;
}

export interface Funder {
	id: string;
	code: string;
	name: MultiLangText;
	fundings: Funding[];
}

export enum FundingStatusCode {
	created = 'created',
	draft = 'draft',
}

export interface Funding {
	id: string;
	name: MultiLangText;
	tags: Tag[];
	status: FundingStatus;
	reportType: ReportType;
	funder: Funder;
}

export interface TagType {
	id: string;
	name: MultiLangText;
	allowMultipleTag: boolean;
	tagValues: TagValue[];
}

export interface TagValue {
	id: string;
	tagType: TagType;
	valueName: MultiLangText;
	subvalueName?: MultiLangText;
}

export interface Tag {
	tagValue: TagValue;
	tagTypeName: MultiLangText;
	valueName: MultiLangText;
	subvalueName?: MultiLangText;
	status: FundingStatusCode;
	reportType: ReportType;
	funder: Funder;
	createdAt: Date;
	updatedAt: Date;
}

export interface TagValueItem {
	id: string;
	name: MultiLangText;
	hasSubValues: boolean;
	allTagValueIds: string[];
}

export enum ProgramStatusCode {
	draft = 'draft',
	published = 'published',
}

export interface ProgramFormApprover {
	userId: string;
	name: string;
}

export interface Program {
	id: string;
	code?: string;
	status: ProgramStatusCode;
	ngoId: number;
	title?: MultiLangText;
	description?: MultiLangText;
	programStart?: Date;
	programEnd?: Date;
	ownerCenterIds: number[] | null; //null means unset
	picUserId?: string | null; //store data only, not used
	picName?: string | null;
	picJobTitle?: string | null;
	picEmail?: string | null;
	picPhoneNumber?: string | null;

	proposalTemplate: ProgramFormTemplate;
	advancePaymentRequestTemplate: ProgramFormTemplate;
	evaluationTemplate: ProgramFormTemplate;
	reimbursementRequestTemplate: ProgramFormTemplate;

	funderId?: string | null; //for saving
	fundingId?: string | null; //for saving
	funding?: Funding; //for loading
	fundingCurrency?: string;
	fundingAmount?: number | null;
	reportNamePrefix?: string | null;
	memberships?: string[] | null;

	targetSubDistricts?: string[] | null;
	notes?: string;

	proposalTemplateId?: string;
	advancePaymentRequestTemplateId?: string;
	evaluationTemplateId?: string;
	reimbursementRequestTemplateId?: string;
	approvalLevel: number;
	approversLevel1?: string[] | null; //userIds
	approversLevel2?: string[] | null;
	approversLevel3?: string[] | null;
	approversFinancial?: string[] | null;
	approvalAmountLimit?: number | null; // in cents
	validations?: ProgramValidations;
	hasEvents: boolean;

	enablers: string[] | null;

	createdAt: Date;
	updatedAt: Date;
}

export interface ProgramDto extends Partial<Omit<Program, 'string'>> {
	id?: string;
	createdAt?: Date;
	updatedAt?: Date;
}

export function EmptyProgramFactory(
	ownerCenterId: number,
	description: MultiLangText,
	proposalTemplateId: string | undefined,
	advancePaymentRequestTemplateId: string | undefined,
	evaluationTemplateId: string | undefined,
	reimbursementRequestTemplateId: string | undefined,
): CreateProgramDto {
	return {
		status: ProgramStatusCode.draft,
		title: { ...EmptyMultiLangText },
		description,
		programStart: undefined,
		programEnd: undefined,
		ownerCenterIds: [ownerCenterId],
		picUserId: undefined,
		picName: undefined,
		picJobTitle: undefined,
		picEmail: undefined,
		picPhoneNumber: undefined,

		fundingId: null,
		fundingCurrency: undefined,
		fundingAmount: undefined,
		memberships: undefined,
		reportNamePrefix: undefined,

		proposalTemplateId: proposalTemplateId,
		advancePaymentRequestTemplateId: advancePaymentRequestTemplateId,
		evaluationTemplateId: evaluationTemplateId,
		reimbursementRequestTemplateId: reimbursementRequestTemplateId,
		approvalLevel: 1,
		approvalAmountLimit: 0,

		targetSubDistricts: undefined,
		notes: undefined,
	};
}

export interface CreateProgramDto
	extends Partial<Omit<Program, 'createdAt' | 'updatedAt'>> {
	id?: string;
	hasEvents?: boolean;
}

export type UpdateProgramDto = Omit<
	Program,
	| 'id'
	| 'funding'
	| 'proposalTemplate'
	| 'advancePaymentRequestTemplate'
	| 'evaluationTemplate'
	| 'reimbursementRequestTemplate'
	| 'hasEvents'
	| 'createdAt'
	| 'updatedAt'
>;

export import ProgramValidationStatus = MultiStepFormValidationStatus;

export enum ProgramEditStep {
	overall = 'overall', // used for checking if program is valid for publish, independent to statuses shown in UI
	basicInfo = 'basicInfo',
	fundingDetails = 'fundingDetails',
	otherDetails = 'otherDetails',
	programForms = 'programForms',
}

export const ProgramStatusChipStyle = {
	[ProgramStatusCode.draft]: NEUTRAL_CHIP,
	[ProgramStatusCode.published]: ACTIVE_CHIP,
};

export const PROGRAM_STEPS = [
	ProgramEditStep.basicInfo,
	ProgramEditStep.fundingDetails,
	ProgramEditStep.otherDetails,
	ProgramEditStep.programForms,
];

export enum ProgramMode {
	view = 'view',
	edit = 'edit',
}

export enum ProgramFormMode {
	approve = 'approve',
	edit = 'edit',
	// view = 'view',
}

export interface ProgramReport {
	id: string;
	title: string;
	reportType: ReportType;
	reportStart: Date;
	reportEnd: Date;
	submissionDueDate: Date;
	numberOfEvents: number;
	createdAt: Date;
	updatedAt: Date;
}

export interface ProgramEvent {
	id: string;
	eventId: string;
	eventType: ProgramEventType;
	eventTitle: MultiLangText;
	eventStatus: JobStatusCode | EventStatusCode;
	ownerCenterId: number;
	collaboratorCenterIds: number[] | null;
	eventTags: ProgramEventTag[];
	proposalId: string | null;
	createdBy: string;
	createdByName: string;
	createdAt: Date;
	updatedAt: Date;
}

export enum ProgramEventType {
	volunteering = 'volunteering',
	activity = 'activity',
}

export interface ProgramEventTag {
	id: string;
	programEventId: string;
	tagValue: EventTagValue;
	tagValueId: string;
}

export interface EventTagValue extends Partial<Omit<TagValue, 'tagType'>> {
	id: string;
}

export interface EventTagDto {
	tagValueId: string;
}

export interface ProgramReportUpdateDto {
	reportTypeId: string;
}

export interface ProgramEventCreateDto {
	reportId: string;
	eventId: string;
	eventType: ProgramEventType;
	eventTags: EventTagDto[];
	proposalId: string;
	createdBy: string;
	createdByName: string;
}

export interface ProgramEventUpdateDto {
	eventId: string;
	eventTags: EventTagDto[];
	proposalId: string;
}

export interface ProgramFunderReport {
	id: string;
	programReportId: string;
	filename: string;
	uploadedBy: string;
	uploadedByName: string;
	uploadedAt: Date;
	url: string;
}

export interface ProgramFunderReportCreateDto {
	programReportId: string;
	filename: string;
	url: string;
	uploadedBy: string;
	uploadedByName: string;
}

export const APPROVAL_STEP: ProgramFormStep = {
	formId: 'approval',
	descKey: 'pages.programs.programForms.step.approvalProgress.label',
};

export interface ProgramFormStep {
	formId: string;
	desc?: MultiLangText;
	descKey?: string;
}

export interface ProgramFormSettings {
	steps: ProgramFormStep[];
	stepForms: {
		[formId: string]: {
			extraFields: ExtraFields;
		};
	};
	remarks: MultiLangText;
}

export enum ProgramFormTypeCode {
	proposal = 'proposal',
	advancePaymentRequest = 'advancePaymentRequest',
	evaluation = 'evaluation',
	reimbursementRequest = 'reimbursementRequest',
}

export interface ProgramFormTemplate {
	name: MultiLangText;
	formType: ProgramFormTypeCode;
	settings: ProgramFormSettings;
	portalTableColumns: string;
	id: string;
	permissions: any[];
}

export type ProgramFormValidations = Partial<
	Record<string, ProgramValidationStatus>
>;

export class ProgramFormData {
	[mapId: string]: any;
}

export enum ProgramFormStatusCode {
	draft = 'draft',
	submitted = 'submitted', //a.k.a pending
	approved = 'approved',
	rejected = 'rejected',
	completed = 'completed',
}

export enum ApprovalActionCode {
	approve = 'approve',
	reject = 'reject',
}

export const ProgramFormTypeToEndpointPathNameMap = {
	[ProgramFormTypeCode.proposal]: 'proposals',
	[ProgramFormTypeCode.advancePaymentRequest]: 'advance-payment-requests',
	[ProgramFormTypeCode.evaluation]: 'evaluations',
	[ProgramFormTypeCode.reimbursementRequest]: 'reimbursement-requests',
};

export const AllApprovalActionCodes = [
	ApprovalActionCode.approve,
	ApprovalActionCode.reject,
];

export interface ApprovalHistoryEntry {
	action: ApprovalActionCode;
	level: number;
	date: string;
	remark: string;
	userId: string;
	type: ApprovalHistoryType;
}

export enum ApprovalHistoryType {
	finance = 'finance',
	normal = 'normal',
}

export interface FormApproval {
	id?: string;
	status: ProgramFormStatusCode;
	submittedBy?: string;
	requiredApprovalLevel: number;
	approvalStage?: number;
	currentApprovers?: string[];
	approvalHistory: ApprovalHistoryEntry[];
}

export interface ApprovalActionDto {
	action: ApprovalActionCode;
	targetStage: number;
	remark: string;
}

export enum EndorsementStatus {
	notNotified = 'notNotified',
	notified = 'notified',
	endorsed = 'endorsed',
	rejected = 'rejected',
}

export interface Endorsement {
	id: string;
	email: string;
	status: EndorsementStatus;
	createdAt: Date;
	updatedAt: Date;
}

export interface EndorsementCount {
	endorsed: number;
	enablers: number;
}

export interface Proposal extends FormApproval {
	id: string;
	code: string;
	program: Program;
	programId: string;
	proposalTemplateId: string;
	proposalData: FormData;
	validations: ProgramFormValidations;
	evaluation: Evaluation;
	endorsements: Endorsement[];
	endorsementCount: EndorsementCount;
	advancePaymentRequests: AdvancePaymentRequest[];
	createdAt: Date;
	updatedAt: Date;
	otherLeader: string[];
}

export interface Evaluation extends FormApproval {
	id: string;
	proposalId: string;
	evaluationTemplateId: string;
	reimbursementTemplateId: string;
	reimbursementTemplate?: ProgramFormTemplate;
	evaluationData: FormData;
	validations: ProgramFormValidations;
	createdAt: Date;
	updatedAt: Date;
	submittedAt: Date;
	submittedBy: string;
}

export interface AdvancePaymentRequest extends FormApproval {
	id: string;
	requestId?: string;
	proposalId: string;
	advancePaymentRequestTemplateId: string;
	advancePaymentRequestData: FormData;
	validations: ProgramFormValidations;
	createdAt: Date;
	updatedAt: Date;
	advancePaymentRequestTemplate?: ProgramFormTemplate;
	canDownload?: boolean;
}

export interface ApprovalStep {
	desc: string;
	approversIds?: string[];
	completed: boolean;
	descParam?: { [key: string]: number };
}

export const ProgramFormApprovalChipStyle = {
	[ProgramFormStatusCode.approved]: ACTIVE_CHIP,
	[ProgramFormStatusCode.submitted]: NEUTRAL_CHIP,
	[ProgramFormStatusCode.draft]: NEUTRAL_CHIP,
	[ProgramFormStatusCode.rejected]: NEGATIVE_CHIP,
	[ProgramFormStatusCode.completed]: ACTIVE_CHIP,
};

export const ProgramFormApprovalActionChipStyle = {
	[ApprovalActionCode.approve]: ACTIVE_CHIP,
	[ApprovalActionCode.reject]: NEGATIVE_CHIP,
};

function initDefaultValuesForForm(
	settings: ProgramFormSettings,
	formData: any,
) {
	settings.steps.forEach(step => {
		const fields = settings.stepForms[step.formId]?.extraFields.fields ?? [];
		Object.keys(fields).forEach(key => {
			if (
				fields[key].default != undefined &&
				fields[key].type !== BasicFieldType.ARRAY
			) {
				if (fields[key].type === BasicFieldType.CHECKBOX) {
					formData[key] = Boolean(fields[key].default);
				} else {
					formData[key] = fields[key].default;
				}
			}
		});
	});
}

export interface ProposalCreateDto {
	programId: string;
	proposalTemplateId: string;
	proposalData: ProgramFormData;
	status: ProgramFormStatusCode;
	requiredApprovalLevel?: number;
	validations: ProgramFormValidations;
}

export interface ProposalUpdateDto {
	programId: string;
	proposalData: ProgramFormData;
	status: ProgramFormStatusCode;
	validations: ProgramFormValidations;
	requiredApprovalLevel?: number;
}

export function EmptyProposalFactory(
	programId: string,
	proposalTemplate: ProgramFormTemplate,
	requiredApprovalLevel: number,
): ProposalCreateDto {
	const proposalData: { [mapId: string]: any } = {};

	initDefaultValuesForForm(proposalTemplate.settings, proposalData);

	return {
		programId,
		proposalTemplateId: proposalTemplate.id,
		proposalData,
		status: ProgramFormStatusCode.draft,
		validations: {},
		requiredApprovalLevel,
	};
}

export interface AdvancePaymentRequestCreateDto {
	proposalId: string;
	advancePaymentRequestTemplateId: string;
	advancePaymentRequestData: ProgramFormData;
	status: ProgramFormStatusCode;
	requiredApprovalLevel?: number;
	validations: ProgramFormValidations;
}

export interface AdvancePaymentRequestUpdateDto {
	advancePaymentRequestData: ProgramFormData;
	proposalId: string;
	status: ProgramFormStatusCode;
	validations: ProgramFormValidations;
	requiredApprovalLevel?: number;
}

export function EmptyAdvancePaymentRequestFactory(
	proposalId: string,
	advancePaymentRequestTemplate: ProgramFormTemplate,
	requiredApprovalLevel: number,
): AdvancePaymentRequestCreateDto {
	const formData: { [mapId: string]: any } = {};

	initDefaultValuesForForm(advancePaymentRequestTemplate.settings, formData);

	return {
		proposalId,
		advancePaymentRequestTemplateId: advancePaymentRequestTemplate.id,
		advancePaymentRequestData: formData,
		status: ProgramFormStatusCode.draft,
		validations: {},
		requiredApprovalLevel,
	};
}

export enum QuantitativeEvaluationField {
	numEvents = 'numEvents',
	numApplications = 'numApplications',
	numSessions = 'numSessions',
	numUniqueParticipants = 'numUniqueParticipants',
	numAttendance = 'numAttendance',
	numVolunteerHours = 'numVolunteerHours',
}

export interface EvaluationCreateDto {
	proposalId: string;
	evaluationTemplateId: string;
	reimbursementTemplateId?: string;
	evaluationData?: ProgramFormData;
	evaluationDataCompressed?: string;
	status: ProgramFormStatusCode;
	requiredApprovalLevel?: number;
	validations: ProgramFormValidations;
}

export interface EvaluationUpdateDto {
	evaluationData?: ProgramFormData;
	evaluationDataCompressed?: string;
	proposalId: string;
	status: ProgramFormStatusCode;
	validations: ProgramFormValidations;
	requiredApprovalLevel?: number;
}

export function EmptyEvaluationFactory(
	proposalId: string,
	evaluationTemplate: ProgramFormTemplate,
	requiredApprovalLevel: number,
	reimbursementTemplateId?: string,
): EvaluationCreateDto {
	const formData: { [mapId: string]: any } = {};

	initDefaultValuesForForm(evaluationTemplate.settings, formData);

	return {
		proposalId,
		evaluationTemplateId: evaluationTemplate.id,
		reimbursementTemplateId,
		evaluationData: formData,
		status: ProgramFormStatusCode.draft,
		validations: {},
		requiredApprovalLevel,
	};
}

export enum EnablerEndorsementStatus {
	notNotified = 'notNotified',
	notified = 'notified',
	endorsed = 'endorsed',
	rejected = 'rejected',
}

export const ProgramFormLoadingSkeletonType = [
	'heading',
	'text',
	'text',
	'heading',
	'text',
	'text',
	'text',
	'text',
	'text',
	'heading',
	'text',
	'text',
	'text',
	'text',
	'text',
	'heading',
	'text',
	'text',
	'text',
	'text',
	'text',
];
